import { Controller } from "@hotwired/stimulus"
import { smoothMapOptions, enableSmoothMapZoom } from "../map_helpers"

export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "address"]

  connect() {
    this.initializeMap()
  }

  initializeMap() {
    if (typeof L === 'undefined') {
      setTimeout(() => this.initializeMap(), 100)
      return
    }

    const savedLat = parseFloat(this.latitudeTarget.value)
    const savedLng = parseFloat(this.longitudeTarget.value)
    
    const lat = isNaN(savedLat) ? 51.505 : savedLat
    const lng = isNaN(savedLng) ? -0.09 : savedLng
    const zoom = isNaN(savedLat) ? 13 : 15

    this.map = L.map(this.mapTarget, {
      dragging: false,
      touchZoom: false,
      ...smoothMapOptions()
    }).setView([lat, lng], zoom)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map)

    this.marker.on('dragend', (e) => {
      const position = e.target.getLatLng()
      this.updateCoordinates(position.lat, position.lng)
      this.reverseGeocode(position.lat, position.lng)
    })

    // Activate on click
    this.map.on('click', (e) => {
      this.activateInteractions()
      const { lat, lng } = e.latlng
      this.marker.setLatLng([lat, lng])
      this.updateCoordinates(lat, lng)
      this.reverseGeocode(lat, lng)
    })

    this.map.on('zoomstart', () => this.activateInteractions())

    this.addInteractionOverlay()
  }

  addInteractionOverlay() {
    if (this.interactionsActive) return;
    
    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0 z-[1000] flex items-center justify-center bg-black/5 pointer-events-none transition-opacity duration-300';
    overlay.innerHTML = `
      <div class="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2">
        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path></svg>
        <span class="text-[10px] font-black uppercase tracking-widest text-gray-900">Click to interact</span>
      </div>
    `;
    this.mapTarget.style.position = 'relative';
    this.mapTarget.appendChild(overlay);
    this.overlay = overlay;
  }

  activateInteractions() {
    if (!this.map || this.interactionsActive) return;
    this.map.dragging.enable();
    this.map.touchZoom.enable();
    enableSmoothMapZoom(this.map);
    this.interactionsActive = true;
    
    if (this.overlay) {
      this.overlay.classList.add('opacity-0');
      setTimeout(() => this.overlay.remove(), 300);
    }
    
    this.mapTarget.style.cursor = 'grab';
  }

  // This is called when the city selector emits a "city-selected" event
  centerOnCity(event) {
    const { country, city } = event.detail
    console.log(`Location Picker: Centering map on ${city}, ${country}`)

    const query = `${city}, ${country}`
    this.geocode(query)
  }

  async geocode(query, fromInput = false) {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      const data = await response.json()

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0]
        const latitude = parseFloat(lat)
        const longitude = parseFloat(lon)

        console.log(`Location Picker: Found coordinates for ${query}: ${latitude}, ${longitude}`)

        // Move the map and marker
        const zoom = fromInput ? 16 : 13
        this.map.setView([latitude, longitude], zoom)
        this.marker.setLatLng([latitude, longitude])
        
        // Update hidden fields
        this.updateCoordinates(latitude, longitude)
        
        // Update the address field with the cleaner display name if it's currently empty
        // Or if it was triggered by the city selector
        if (!this.addressTarget.value || !fromInput) {
          this.addressTarget.value = display_name
        }
      } else {
        console.warn(`Location Picker: No results found for geocoding query: ${query}`)
      }
    } catch (error) {
      console.error("Location Picker: Geocoding failed:", error)
    }
  }

  handleAddressInput(event) {
    const query = event.target.value
    if (query.length < 3) return

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      console.log(`Location Picker: Geocoding from input: ${query}`)
      this.geocode(query, true) // Pass true to indicate it's from manual input
    }, 800)
  }

  updateCoordinates(lat, lng) {
    this.latitudeTarget.value = lat
    this.longitudeTarget.value = lng
    console.log(`Coordinates updated: ${lat}, ${lng}`)
  }

  async reverseGeocode(lat, lng) {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
      const data = await response.json()
      if (data.display_name) {
        this.addressTarget.value = data.display_name
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error)
    }
  }
}
